import '.i{display:flex;}.ssr-plugin'
import '.m{flex-direction:column;}.ssr-plugin'
import '.v{gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import useMediaQuery from '@app/util/useMediaQuery'
import { Nutshell } from '@app/components/blocks/nutshell/Nutshell'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import Row from '@app/components/layout/Row'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { styled } from 'inlines'
import { useQuery } from '@based/sdk'
import { navigate } from 'wouter/use-browser-location'
import { ThreadLatestViewModel } from 'functions/app/thread/view-latest'
import { labelAddressBuilder, fullName } from '@app/util/helpers'
import useDateFormat from '@app/util/useDateFormat'
import { RegionalListInitiativeModel } from 'functions/app/initiative/list-regional'
import { RegionalListPlotModel } from 'functions/app/plot/list-regional'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { GeoViewModel } from './models'
import { regionsTabsQueryParamName, regionsTabs } from '../../constants'

export const Overview = ({ entity }: { entity: GeoViewModel }) => {
  const isRegion = 'municipalities' in entity
  const isMobile = useMediaQuery()
  const largerGap = isMobile ? '2rem' : '3.5rem'
  const translations = useTranslations().app
  const dateFormat = useDateFormat()

  const listPayload = isRegion
    ? { regionId: entity.id, top: 2 }
    : { municipalityId: entity.id, top: 2 }

  const discussionsQuery = useQuery('view-latest-thread', {
    id: entity.thread?.id,
  })
  const discussions: ThreadLatestViewModel = discussionsQuery.data ?? {
    posts: [],
  }

  const initiativeQuery = useQuery('list-regional-initiative', listPayload)
  const initiatives: RegionalListInitiativeModel[] = initiativeQuery.data ?? []

  const plotQuery = useQuery('list-regional-plot', listPayload)
  const plots: RegionalListPlotModel[] = plotQuery.data ?? []

  return (
    <Column gap={largerGap} alignItems="stretch">
      <div className="i m v"
      >
        <SafeHtml html={entity?.content} />
        {/* {entity.contentBlocks && (
          <ContentSections
            sections={entity?.contentBlocks.map((b) => ({
              ...b,
              truncationThreshold: 5,
            }))}
          />
        )} */}
      </div>
      <Nutshell
        heading={entity.name + translations.regionPage.nutshell.inAnutshell}
        items={[
          {
            total: entity.initiatives?.count ?? 0,
            subtitle: 'Initiatieven',
          },
          {
            total: entity.plots?.count ?? 0,
            subtitle: 'Location',
          },
          ...(isRegion
            ? [
                {
                  total: entity.municipalities?.length ?? 0,
                  subtitle: 'Gemeenten',
                },
              ]
            : []),
          {
            total: entity.serviceProviders?.count ?? 0,
            subtitle: 'Experts',
          },
        ]}
        style={{
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      />

      {discussions?.posts?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={translations.regionPage.latestDiscussions}
            seeMore={translations.common.seeMore}
            toRoute={`?${regionsTabsQueryParamName}=${regionsTabs.discussion}`}
          />
          <Row style={{ gap: '1rem' }}>
            {discussions.posts.map((p) => (
              <DiscussionCard
                key={p.id}
                authorName={
                  p.author ? fullName(p.author) : translations.common.unknown
                }
                authorAvatar={p.author?.avatar}
                date={p.createdAt ? dateFormat(new Date(p.createdAt)) : ''}
                content={p.content ?? ''}
                likes={p.viewers?.count ?? 0}
                replies={p.replies?.count ?? 0}
                style={{ flex: 1 }}
              />
            ))}
          </Row>
        </Column>
      )}

      {initiatives?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={translations.regionPage.popularInitiatives}
            seeMore={translations.common.seeMore}
            toRoute={`?${regionsTabsQueryParamName}=${regionsTabs.initiatives}`}
          />
          <div className="i v"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {initiatives.map((initiative) => (
              <InitiativeCard
                key={initiative.slug}
                to={pages.initiative(initiative.slug)}
                image={initiative.images?.[0]?.src ?? defaultProjectPicture}
                title={initiative.title}
                description={initiative.description}
                location={labelAddressBuilder(
                  initiative.location?.address,
                  translations.common.notSpecified,
                )}
                phase={initiative.phase?.name}
                tagText={
                  initiative.searchingForMembers
                    ? translations.common.initiativeStatus.searchingForMembers
                    : translations.common.initiativeStatus
                        .notSearchingForMembers
                }
                tagVariant={initiative.searchingForMembers ? 'green' : 'gray'}
              />
            ))}
          </div>
        </Column>
      )}
      {plots?.length > 0 && (
        <Column alignItems="stretch" gap={0}>
          <SectionHeader
            title={translations.regionPage.locations}
            seeMore={translations.common.seeMore}
            toRoute={`?${regionsTabsQueryParamName}=${regionsTabs.locations}`}
          />
          <div className="i v"
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {plots.map((plot) => (
              <InitiativeCard
                key={plot.slug}
                image={plot.media?.[0]?.src ?? defaultProjectPicture}
                title={plot.name}
                description={plot.description}
                location={labelAddressBuilder(
                  plot.location?.address,
                  translations.common.notSpecified,
                )}
                tagText={
                  translations.common.plotApplicationStatus[
                    plot.applicationStatus
                  ]
                }
                tagVariant={
                  plot.applicationStatus === 'applicationOpen'
                    ? 'green'
                    : 'gray'
                }
                onClick={() => navigate(pages.plot(plot.slug))}
              />
            ))}
          </div>
        </Column>
      )}

      {/* <Column alignItems="stretch" gap={0}>
        <SectionHeader
          title={translations.regionPage.current}
          seeMore={translations.common.seeMore}
          toRoute={pages.initiatives}
        />
        <Row>
          <Column>
            <BlogCard
              image="https://images.pexels.com/photos/2917838/pexels-photo-2917838.jpeg"
              meta="Nieuws"
              title="Tinyhouses Westpark het groenste kleine wijkje van Stad"
            />
          </Column>

          <Column>
            <Event
              date={26}
              month="Augustus"
              category="INFORMATIE"
              title="Informatiebijeenkomst over CPO-bouwen in Mill"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
            <Event
              date={14}
              month="Oktober"
              category="INFORMATIE"
              title="Open dag Twee Amsterdam"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
            <Event
              date={14}
              month="Oktober"
              category="INFORMATIE"
              title="Informatiebijeenkomst over CPO-bouwen in Mill"
              time="10:00 - 12:00"
              location="Amsterdam"
            />
          </Column>
        </Row>
      </Column> */}
    </Column>
  )
}
